import { Checkbox } from "@mantine/core"
import { IconChevronRight } from "@tabler/icons-react"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { Button, Missing } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { ProductResponses } from "@tfg/types/endpoints/products"

import { TranslatedTextContent } from "~/components/TranslatedTextContent.tsx"
import { CertificatesCell } from "~/components/cells/CertificatesCell.tsx"
import { CodeCell } from "~/components/cells/CodeCell.tsx"
import { DocumentActionsCell } from "~/components/cells/DocumentActionsCell.tsx"
import { CompositionCell } from "~/pages/Brand/Regulations/RDUE/CompositionCell.tsx"

const columnHelper = createColumnHelper<ProductResponses.RDUEProduct>()
export const useRdueColumns = (
  maxComponentCount: number,
  viewComponentDetails: (component: ProductResponses.RDUEComponent) => void
) => {
  const { t } = useTranslation("regulations")

  return useMemo<ColumnDef<ProductResponses.RDUEProduct, any>[]>(
    () => [
      columnHelper.display({
        id: "select",
        cell: ({ row }) => (
          <Checkbox
            onClick={(e) => {
              e.stopPropagation()
              row.toggleSelected(e.currentTarget.checked)
            }}
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            color="blue"
          />
        ),
      }),
      columnHelper.display({
        header: t("columns.rdue.documents"),
        cell: ({ row }) =>
          row.original.dueDiligenceDocumentId ? (
            <DocumentActionsCell
              documentName={t("columns.rdue.dueDiligence")}
              documentId={row.original.dueDiligenceDocumentId}
            />
          ) : (
            <Missing />
          ),
      }),
      columnHelper.accessor("name", {
        header: t("columns.rdue.name"),
      }),
      columnHelper.accessor("rdueCompliance", {
        header: t("columns.rdue.compliant"),
      }),
      columnHelper.accessor("brandReference", {
        header: t("columns.rdue.brandReference"),
      }),
      columnHelper.accessor("purchaseOrder", {
        header: t("columns.rdue.purchaseOrder"),
        cell: (info) => {
          info.getValue() ? (
            <CodeCell value={info.getValue()?.reference} />
          ) : (
            <Missing />
          )
        },
      }),
      columnHelper.accessor("quantity", {
        header: t("columns.rdue.quantity"),
      }),
      columnHelper.accessor("composition", {
        header: t("columns.rdue.composition"),
        cell: ({ getValue }) => <CompositionCell composition={getValue()} />,
      }),
      columnHelper.accessor("relevantCommodity", {
        header: t("columns.rdue.relevantCommodity"),
      }),
      columnHelper.accessor("hsCode", {
        header: t("columns.rdue.hsCode"),
      }),
      ...Array.from({ length: maxComponentCount }).flatMap((_, index) => {
        return [
          columnHelper.accessor(`components`, {
            header: t("columns.rdue.component_details", {
              index: (index + 1).toString(),
            }),
            id: `component_details-${index}`,
            cell: (info) => {
              const components = info.getValue()
              const component =
                components.find(
                  (component) => component.number === index + 1
                ) ?? (components.length > index ? components[index] : undefined)
              return component ? (
                <Button
                  onClick={() => {
                    viewComponentDetails(component)
                  }}
                  variant="tertiary"
                  color="black"
                  fw="normal"
                  w={200}
                  px={0}
                  justify="space-between"
                  rightSection={<IconChevronRight size={13} />}
                >
                  <TranslatedTextContent
                    textContent={component.typeTextContent}
                  />
                </Button>
              ) : (
                <Missing />
              )
            },
          }),
          columnHelper.accessor(`components`, {
            header: t("columns.rdue.component_certificates", {
              index: (index + 1).toString(),
            }),
            id: `component_certificates-${index}`,
            cell: (info) => {
              const components = info.getValue()
              const component =
                components.find(
                  (component) => component.number === index + 1
                ) ?? (components.length > index ? components[index] : undefined)
              return component ? (
                <CertificatesCell
                  certificates={component.certificates}
                  key={index}
                />
              ) : (
                <Missing />
              )
            },
          }),
        ]
      }),
    ],
    [t, maxComponentCount, viewComponentDetails]
  )
}
